import PERMISSIONS from '@/constants/permissions';
import { useLeadActivities } from '@/hooks/useLeadActivities';
import { postCommentToLeadRequest } from '@/services/api/lead';
import { LeadDetailsModel } from '@/typings/models/Lead';
import { LeadActivityEvents } from '@/typings/models/LeadActivity';
import { CheckAccess } from '@/utils/access';
import { F, getIntlTranslation, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import { SendOutlined } from '@ant-design/icons';
import { Button, Empty, Input, Spin, Timeline } from 'antd';
import type { InputRef } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'umi';
import ActivityCard from './ActivityCard';
import { getActivityPresentation } from './activityPresentation';
import styles from './index.less';
import { TONE_CLASS } from './LeadActivity';
import { groupActivitiesByDay } from './utils';

/**
 * Comments as the same card timeline the activity log uses: day-grouped sticky
 * headers, an icon rail, and one ActivityCard per comment (text + "date, time
 * · author" footer). Newest first, with the composer pinned above the feed so
 * a new comment appears right under it.
 */
const LeadComments = ({
  leadDetails,
  leadId,
  autoFocus,
  onPosted,
  hideEmpty,
  prefill,
}: {
  leadDetails: LeadDetailsModel;
  leadId?: string;
  autoFocus?: boolean;
  onPosted?: () => void;
  /** Skip the "No comments yet" placeholder — the composer alone is enough */
  hideEmpty?: boolean;
  /** Pre-fill + focus the composer (task actions' open_add_comment); a new
   * `at` re-applies the same text */
  prefill?: { text: string; at?: number } | null;
}) => {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<InputRef>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [commentValue, setCommentValue] = useState<string>('');

  useEffect(() => {
    if (!prefill?.at) return;
    // Empty prefill text still means "open the composer": focus without
    // clobbering whatever the user may have typed already
    if (prefill.text) setCommentValue(prefill.text);
    composerRef.current?.focus({ cursor: 'end' });
  }, [prefill?.at]);

  const resolvedLeadId = leadDetails?.id || leadId;
  const { comments, loading, paginationLoading, refresh } = useLeadActivities({
    leadId: resolvedLeadId,
    containerRef,
  });

  // The API orders by id; sort by date so the day groups always run
  // newest-first even when ids and timestamps disagree.
  const groups = useMemo(() => {
    const sorted = [...comments].sort((a, b) =>
      (b.created || '').localeCompare(a.created || ''),
    );
    return groupActivitiesByDay(sorted, intl);
  }, [comments, intl]);

  const { icon, tone } = getActivityPresentation(LeadActivityEvents.comment);

  const onSubmit = () => {
    const comment = commentValue.trim();
    if (!comment || submitLoading) return;

    setSubmitLoading(true);
    postCommentToLeadRequest({
      params: { leadId: resolvedLeadId },
      data: { comment },
    }).then((APIResponse) => {
      processAPIResponseNow(APIResponse, {
        onSuccess() {
          setCommentValue('');
          refresh();
          onPosted?.();
        },
        onEnd() {
          setSubmitLoading(false);
        },
      });
    });
  };

  return (
    <div className={styles.commentsSection}>
      <CheckAccess
        access={[PERMISSIONS.Leads.EDIT, PERMISSIONS.BUSINESS_PARTNER]}
      >
        <div className={styles.commentComposer}>
          <Input.TextArea
            ref={composerRef}
            autoFocus={autoFocus}
            autoSize={{ minRows: 2, maxRows: 6 }}
            placeholder={getIntlTranslation(intl, {
              id: 'pages.lead.comments.placeholder',
              defaultMessage: 'Write a comment…',
            })}
            value={commentValue}
            disabled={submitLoading}
            onChange={(e) => setCommentValue(e.target.value)}
            onPressEnter={(e) => {
              // Enter sends, Shift+Enter inserts a newline.
              if (!e.shiftKey) {
                e.preventDefault();
                onSubmit();
              }
            }}
          />
          <div className={styles.commentComposerFooter}>
            <span className={styles.commentComposerHint}>
              <F
                id="pages.lead.comments.hint"
                defaultMessage="Enter to send · Shift+Enter for a new line"
              />
            </span>
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={submitLoading}
              disabled={!commentValue.trim()}
              onClick={onSubmit}
            >
              <F entity={INTL_ENTITY.COMMENT} action={INTL_ACTION.ADD} />
            </Button>
          </div>
        </div>
      </CheckAccess>
      <Spin
        spinning={loading}
        className={classNames(styles.activitySpin, styles.commentsSpin)}
      >
        <div ref={containerRef} className={styles.leadActivityContainer}>
          {!loading && comments.length === 0 && !hideEmpty && (
            <Empty
              description={
                <F
                  id="pages.lead.comments.empty"
                  defaultMessage="No comments yet"
                />
              }
            />
          )}
          {groups.map((group, groupIdx) => (
            <div
              key={`${group.key}-${groupIdx}`}
              className={styles.activityDateGroup}
            >
              {group.label && (
                <div className={styles.activityDateHeader}>{group.label}</div>
              )}
              <Timeline
                items={group.items.map((comment, idx) => ({
                  key: comment.id ?? `${group.key}-${idx}`,
                  dot: (
                    <span
                      className={classNames(
                        styles.activityDot,
                        TONE_CLASS[tone],
                      )}
                    >
                      {icon}
                    </span>
                  ),
                  children: <ActivityCard activity={comment} hideTitle />,
                }))}
              />
            </div>
          ))}
          {paginationLoading && (
            <div className={styles.activityPaginationLoader}>
              <Spin size="small" />
            </div>
          )}
        </div>
      </Spin>
    </div>
  );
};

export default LeadComments;
